import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listConnectors, type ListedConnector } from "../../../api/connectorsApi";
import { listRouteOptions } from "../../../api/threads/routeOptions";
import type { AgentRoute, ApiKeyRoute } from "../../../api/threads/liveMessages";
import { appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import ConnectorCommandLine from "../../connectors/ConnectorCommandLine";
import PairCommand from "../../connectors/PairCommand";
import ContextSizeField from "../../connectors/models/ContextSizeField";
import ConnectorSetupButton from "../../connectors/setup/ConnectorSetupButton";
import FallbackRoutesField from "./FallbackRoutesField";
import RouteCapabilityDetails from "./RouteCapabilityDetails";
import RouteFitNotice from "./RouteFitNotice";
import { notOfferedText, unavailabilityOn, withoutEffort } from "../llmChoice/llmOptions";
import { useLlmChoices } from "../llmChoice/useLlmChoices";
import { onlineConnectors, opensWorkspaceToTools, optionOf, routeFitOf, sameRoute, type HostingChoice, type TurnRoute } from "./routeChoices";
import Field from "../../../ui/forms/Field";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ButtonLink from "../../../ui/buttons/ButtonLink";

interface HostingChoiceFormProps {
  /** The Thread that goes live: the server checks each model against its turns (Q-63). */
  threadId: string;
  /** The route, fallbacks and profile the Thread has now, preselected where the connector offers them. */
  current: { route: AgentRoute | ApiKeyRoute | null; fallbackRoutes: readonly TurnRoute[]; permissionProfile: string; connectorId: string | null };
  submitLabel: string;
  submitting: boolean;
  /** Why the Run can't move to another PC now (module C's `move_hosting`): only the PC it runs on is offered, with this. */
  moveRefused?: string;
  /** Why the choice can't be sent now (module C's decision on what it submits): its button stays, unavailable, and says why. */
  submitRefused?: string;
  onSubmit: (choice: HostingChoice) => void;
  onCancel: () => void;
}

/**
 * Choose where a Thread runs (W-07, F55): the connector and the permission profile on it
 * (FR-THREAD-4, FR-CONN-2). What it runs with is the "Runs with" row's (F106): this form says
 * how the Thread's LLM fits the chosen PC, and never chooses one. Without an online connector
 * it says why nothing can be chosen and how to pair one.
 */
export default function HostingChoiceForm(props: HostingChoiceFormProps) {
  return (
    <div className="space-y-4">
      <ConnectorSetupButton />
      <HostingChoices {...props} />
    </div>
  );
}

function HostingChoices(props: HostingChoiceFormProps) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listConnectors(signal), []);
  const { state, reload } = useLoad(load);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("threads.hosting.loadingConnectors")} onRetry={reload} />;
  const online = onlineConnectors(state.value);
  if (online.length === 0) {
    const paired = state.value.some((connector) => connector.state === "active");
    return (
      <div className="space-y-3">
        <StatusMessage tone="info" title={t(paired ? "threads.hosting.noneOnlineTitle" : "threads.hosting.nonePairedTitle")}>
          <p>{t(paired ? "threads.hosting.noneOnlineBody" : "threads.hosting.nonePairedBody")}</p>
          {paired ? (
            <ConnectorCommandLine command={{ args: "run", aboutServer: true }} className="mt-2" />
          ) : (
            <PairCommand className="mt-2" />
          )}
        </StatusMessage>
        <div className="flex flex-wrap gap-2">
          <ButtonLink to={appPaths.connectorPairing()} variant="primary" size="xs">{t("threads.hosting.pairLink")}</ButtonLink>
          <ButtonLink to={appPaths.profileConnectors()} size="xs">{t("threads.hosting.connectorsLink")}</ButtonLink>
          <AppButton variant="secondary" size="xs" onClick={reload}>{t("threads.hosting.checkAgain")}</AppButton>
        </div>
      </div>
    );
  }
  // While it can't move (module C's `move_hosting`), only the PC it runs on, for its settings there.
  const offered = props.moveRefused === undefined ? online : online.filter((connector) => connector.connectorId === props.current.connectorId);
  return <ChoiceFields {...props} connectors={offered} />;
}

function ChoiceFields({ connectors, current, moveRefused, ...rest }: HostingChoiceFormProps & { connectors: readonly ListedConnector[] }) {
  const { t } = useTranslation();
  const [connectorId, setConnectorId] = useState(
    connectors.find((connector) => connector.connectorId === current.connectorId)?.connectorId ?? connectors[0]?.connectorId ?? "",
  );
  const connector = connectors.find((candidate) => candidate.connectorId === connectorId) ?? connectors[0];
  if (connector === undefined) {
    return null;
  }
  return (
    <div className="space-y-4">
      <Field label={t("threads.hosting.connector")}>
        {(control) => (
          <select {...control} className={FORM_SELECT_CLASS} value={connector.connectorId} onChange={(event) => { setConnectorId(event.target.value); }}>
            {connectors.map((candidate) => (
              <option key={candidate.connectorId} value={candidate.connectorId}>
                {candidate.name} · {t("threads.hosting.online")}
              </option>
            ))}
          </select>
        )}
      </Field>
      {moveRefused === undefined ? null : <p className={FORM_HELP_TEXT_CLASS}>{moveRefused}</p>}
      <RouteFields key={connector.connectorId} connector={connector} current={current} {...rest} />
    </div>
  );
}

/** The profile on one connector, and how the Thread's LLM fits it; starts over when it changes. */
function RouteFields({ threadId, connector: listed, current, submitLabel, submitting, submitRefused, onSubmit, onCancel }: HostingChoiceFormProps & { connector: ListedConnector }) {
  const { t } = useTranslation();
  // The PC's report and the route options, read together with the picker's and renewed with its Refresh.
  const optionsOf = useCallback((connectorId: string, signal: AbortSignal) => listRouteOptions(threadId, connectorId, signal), [threadId]);
  const { state: optionsState, refresh, reload } = useLlmChoices([listed.connectorId], optionsOf);
  const loaded = optionsState.status === "loaded" ? optionsState.value[0] : undefined;
  const connector = loaded?.connector ?? listed;
  const options = loaded?.listing.options ?? null;
  const workspaceFileSteps = loaded?.listing.workspaceFileSteps ?? [];
  // The server suggests the profile (Q-81), as it decides the automatic host's (BA12-02);
  // until it has, or if it can't, nothing is chosen for the host.
  const suggestedProfile = loaded?.listing.suggestedPermissionProfile ?? null;
  // The Thread's own LLM: this form shows how it fits the PC and never picks another (W-07).
  const route = current.route;
  const profiles = connector.status.permissionProfiles;
  // `null` until the host picks one: then the suggestion follows the server's answer.
  const [chosenProfile, setProfile] = useState<string | null>(null);
  const profile = chosenProfile ?? suggestedProfile;
  const profileLacksWorkspace = workspaceFileSteps.length > 0 && !opensWorkspaceToTools(profiles.find((candidate) => candidate.name === profile));
  // The server's options name routes without a reasoning effort.
  const offeredRoute = route === null ? null : withoutEffort(route);
  // Whether the default memory tools come off, and whether any route is needed, are the server's answers.
  const dropsDefaultTools = optionOf(options, offeredRoute)?.dropsDefaultReplyTools ?? false;
  const fit = routeFitOf(options, offeredRoute);
  // The server's answer for the Thread's LLM on this PC: one it doesn't run isn't carried to it (F106).
  const unavailable = route === null ? null : unavailabilityOn(route, loaded);
  const offered = route !== null && unavailable === null;
  // The route the choice carries: the Thread's, where it is offered and doesn't lack what the
  // turns need; else none, and the Thread keeps its own as its setting. Going live never waits
  // for an LLM: without one, the "Runs with" row asks for it (W-07).
  const sentRoute = route !== null && offered && fit.kind !== "lacks" ? route : null;
  const [fallbackRoutes, setFallbackRoutes] = useState(current.fallbackRoutes);

  return (
    <>
      {optionsState.status === "loaded" ? null : <LoadStatus state={optionsState} loadingLabel={t("threads.llm.loading")} onRetry={reload} />}
      <Field label={t("threads.hosting.profile")}>
        {(control) => (
          <select {...control} className={FORM_SELECT_CLASS} value={profile ?? ""} onChange={(event) => { setProfile(event.target.value); }}>
            {profile === null ? <option value="" /> : null}
            {(profiles.length === 0 && profile !== null ? [profile] : profiles.map((candidate) => candidate.name)).map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
      </Field>
      {workspaceFileSteps.length > 0 ? (
        <p className={profileLacksWorkspace ? "app-status-text-warning text-xs" : "app-help-text text-xs"}>
          {t(profileLacksWorkspace ? "threads.hosting.workspaceProfileMissing" : "threads.hosting.workspaceProfileChosen", { steps: workspaceFileSteps.join(", ") })}
        </p>
      ) : null}
      {loaded !== undefined && unavailable !== null ? <p className="app-status-text-warning text-xs">{notOfferedText(connector.name, unavailable, t)}</p> : null}
      <RouteFitNotice fit={fit} failed={optionsState.status === "failed"} />
      {dropsDefaultTools ? <p className="app-help-text text-xs">{t("threads.hosting.fit.defaultToolsDropped")}</p> : null}
      <RouteCapabilityDetails option={optionOf(options, offeredRoute)} />
      <ContextSizeField option={optionOf(options, offeredRoute)} />
      <FallbackRoutesField llmChoices={optionsState.status === "loaded" ? optionsState.value : []} onRefresh={refresh} connector={connector} own={route} value={fallbackRoutes} onChange={setFallbackRoutes} />
      <div className="flex justify-end gap-2">
        <AppButton variant="secondary" size="sm" onClick={onCancel} disabled={submitting}>{t("common.cancel")}</AppButton>
        <AppButton
          variant="primary"
          size="sm"
          disabled={submitting || profile === null}
          unavailable={submitRefused}
          onClick={() => {
            if (profile !== null) onSubmit({ connectorId: connector.connectorId, route: sentRoute, permissionProfile: profile, fallbackRoutes: sentRoute === null ? fallbackRoutes : fallbackRoutes.filter((fallback) => !sameRoute(fallback, sentRoute)), dropsDefaultReplyTools: sentRoute !== null && dropsDefaultTools });
          }}
        >
          {submitLabel}
        </AppButton>
      </div>
    </>
  );
}
